import React from "react";
import { Card } from "@/components/ui";
import { safeFormatNumber } from "@/utils";
import { HelpCircle, LucideIcon } from "lucide-react";

export interface HealthStat {
  label?: string;
  value?: number | string;
  prefix?: LucideIcon;
  color?: string;
  labelColor?: string;
}

export interface HealthInfo {
  key: string;
  title: string;
  icon?: LucideIcon;
  primaryValue: HealthStat;
  secondaryValues: HealthStat[];
  footer?: string;
  onClick?: () => void;
}

interface HealthCardProps {
  onClick?: () => void;
  width?: number;
  info: HealthInfo;
}

type Props = HealthCardProps;

const HealthCardFC: React.FC<Props> = ({ info, width }) => {
  const IconComponent = info.icon || HelpCircle;
  const hasOnClick = !!info.onClick;

  return (
    <Card
      key={info.key}
      onClick={info.onClick}
      style={{ width: width || "150px" }}
      className={`rounded flex flex-col justify-between select-none ${
        hasOnClick
          ? "cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.01] active:scale-[0.99]"
          : ""
      }`}
    >
      <Card.Content>
        <div className="flex flex-col gap-2">
          {/* Card Header with Icon */}
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 uppercase tracking-wider">
            <IconComponent className="size-3.5 text-slate-400 shrink-0" />
            <span>{info.title}</span>
          </div>

          {/* Primary Value and Label inline next to each other */}
          <div className="flex items-baseline gap-2 min-w-0">
            <span
              className={`text-3xl font-bold tracking-tight leading-none shrink-0 ${
                info.primaryValue.color || "text-slate-700"
              }`}
            >
              {safeFormatNumber(info.primaryValue.value)}
            </span>
            {info.primaryValue.label && (
              <span
                className={`text-sm font-semibold shrink-0 leading-none ${
                  info.primaryValue.labelColor || "text-slate-500"
                }`}
              >
                {info.primaryValue.label}
              </span>
            )}
          </div>

          {/* Secondary Values */}
          {info.secondaryValues && info.secondaryValues.length > 0 && (
            <div className="flex flex-col gap-1 w-full min-w-0 border-t border-slate-200 pt-2">
              {info.secondaryValues.map((stat, index) => {
                const PrefixIcon = stat.prefix;
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between text-xs text-slate-500 gap-2 min-w-0 w-full"
                  >
                    <span className={`truncate flex-1 text-left ${stat.labelColor || "text-slate-500"}`}>
                      {stat.label}
                    </span>
                    <span
                      className={`font-semibold text-sm flex items-center gap-1 shrink-0 ${
                        stat.color || "text-slate-700"
                      }`}
                    >
                      {PrefixIcon && <PrefixIcon className="size-4 shrink-0" />}
                      {safeFormatNumber(stat.value)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer Label */}
          {info.footer && (
            <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide text-left leading-none">
              {info.footer}
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

export const HealthCard = HealthCardFC;
